import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";

interface UseAccessGateReturn {
  /** True when the user can enter gated workspace areas */
  hasAccess: boolean;
  /** True while the access state is being resolved */
  loading: boolean;
  /** True when the user is signed in but lacks subscription/beta access */
  needsSubscription: boolean;
  /** True when the user is not signed in at all */
  needsSignIn: boolean;
}

/**
 * Determines whether the current user can access gated workspace sections
 * (Command Center, AI Agents, etc.). Admins always pass. Otherwise we look
 * for an active subscription record. Beta testers are granted access by
 * being assigned the `admin` or `reviewer` role, or via an active
 * `user_subscriptions` row managed by an admin.
 */
export function useAccessGate(): UseAccessGateReturn {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, isReviewer, loading: roleLoading } = useUserRole();
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subLoading, setSubLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!user) {
        setHasSubscription(false);
        setSubLoading(false);
        return;
      }
      setSubLoading(true);
      const { data, error } = await supabase
        .from("user_subscriptions")
        .select("id, expires_at, is_active")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .maybeSingle();

      if (cancelled) return;

      if (error || !data) {
        setHasSubscription(false);
      } else {
        const notExpired = !data.expires_at || new Date(data.expires_at) > new Date();
        setHasSubscription(Boolean(data.is_active) && notExpired);
      }
      setSubLoading(false);
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const loading = authLoading || roleLoading || (Boolean(user) && subLoading);
  const privileged = isAdmin || isReviewer || hasSubscription;
  const hasAccess = Boolean(user) && privileged;

  return {
    hasAccess,
    loading,
    needsSubscription: Boolean(user) && !privileged && !loading,
    needsSignIn: !user && !authLoading,
  };
}
